'use client';

import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import { Delete02Icon } from '@terra/ui-icons';
import { ApplicationAutomationIds, automationId } from '@terra/shared-utils';
import { IconButton } from '../../../components/icon-button';
import { Spinner } from '../../../components/spinner';
import { Tooltip } from '../../../components/tooltip';
import type { MemoryStatus } from '@terra/shared-contracts-memory';
import {
  canRemoveEndpointMemory,
  type EndpointMemoryCreationMethod,
} from './can-remove-endpoint-memory';

type EndpointMemoryDeleteButtonProps = {
  creationMethod?: EndpointMemoryCreationMethod;
  status?: MemoryStatus;
  loading: boolean;
  onDelete: () => void;
};

export function EndpointMemoryDeleteButton({
  creationMethod,
  status,
  loading,
  onDelete,
}: EndpointMemoryDeleteButtonProps) {
  const t = useTranslations('designSystem.applicationAssets.drawer.memories');
  const tCommon = useTranslations('designSystem.common');
  const canRemove = canRemoveEndpointMemory({ creationMethod, status });
  const deleteLabel = tCommon('delete');
  const tooltipTitle = canRemove ? deleteLabel : t('systemCannotRemove');

  return (
    <Tooltip title={tooltipTitle}>
      <Box display="inline-flex">
        <IconButton
          aria-label={deleteLabel}
          size="xs"
          variant="ghost"
          disabled={!canRemove || loading}
          {...automationId(
            ApplicationAutomationIds.assetsEndpointDrawer,
            'tab-memories-delete',
          )}
          onClick={(event) => {
            event.stopPropagation();
            if (!canRemove) {
              return;
            }

            onDelete();
          }}
        >
          {loading ? <Spinner size="xs" /> : <Delete02Icon size={14} />}
        </IconButton>
      </Box>
    </Tooltip>
  );
}
